import { ErrorRounded, OpenInNew } from "@mui/icons-material";
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  Tooltip,
  Typography,
} from "@mui/material";
import { isNil } from "lodash";
import PropTypes from "prop-types";
import { useEffect, useState } from "react";
import { useBorrowerPortalHook } from "../../hooks/useBorrowerPortalHook";
import FilePreviewImageTypeIcon from "./FilePreviewImageTypeIcon";

const FilePreview = ({
  fileObject,
  onClose,
  showDownloadButton = true,
  height = "100%",
  width = "100%",
  sx = {},
  // optional overrides for extracted fields:
  contentType: overrideContentType,
  fileDisplayName: overrideDisplayName,
  fileStorageName: overrideStorageName,
  loanId: overrideLoanId,
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const {
    borrowerPortalHookLoading,
    getBorrowerFileById,
    getBorrowerLoanFileById,
  } = useBorrowerPortalHook();

  // derive or override
  const contentType = overrideContentType || fileObject?.content_type;
  const fileDisplayName = overrideDisplayName || fileObject?.file_display_name;
  const fileStorageName = overrideStorageName || fileObject?.file_storage_name;
  const loanId =
    overrideLoanId || fileObject?.dealId || fileObject?.containerName;

  useEffect(
    () => () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    },
    [previewUrl],
  );

  useEffect(() => {
    if (
      fileObject &&
      fileStorageName &&
      loanId &&
      loanId !== "undefined" &&
      loanId !== "None" &&
      loanId !== "none"
    ) {
      setLoading(true);
      setError(null);
      getBorrowerLoanFileById({
        loanId,
        file_storage_name: fileStorageName,
        isFile: true,
        onSuccessFn: (data) => {
          try {
            const blob =
              data instanceof Blob
                ? data
                : new Blob([data], { type: contentType });
            setPreviewUrl(URL.createObjectURL(blob));
          } catch (e) {
            console.error("Blob URL error:", e);
            setError("Unable to process file preview.");
          } finally {
            setLoading(false);
          }
        },
        onErrorFn: (err) => {
          console.error("Fetch error:", err);
          setError("Failed to load file.");
          setLoading(false);
        },
      });
    }

    if (fileObject && fileStorageName && (isNil(loanId) || loanId === "None")) {
      setLoading(true);
      setError(null);
      getBorrowerFileById({
        file_storage_name: fileStorageName,
        isFile: true,
        onSuccessFn: (data) => {
          try {
            const blob =
              data instanceof Blob
                ? data
                : new Blob([data], { type: contentType });
            setPreviewUrl(URL.createObjectURL(blob));
          } catch (e) {
            console.error("Blob URL error:", e);
            setError("Unable to process file preview.");
          } finally {
            setLoading(false);
          }
        },
        onErrorFn: (err) => {
          console.error("Fetch error:", err);
          setError("Failed to load file.");
          setLoading(false);
        },
      });
    }
  }, [fileObject]);

  if (isNil(fileObject)) return null;

  const isImage = contentType?.startsWith("image/");
  const isPdf = contentType === "application/pdf";
  const canOpenInNewTab = !loading && !error && !!previewUrl;

  const handleOpenInNewTab = () => {
    getBorrowerLoanFileById({
      loanId,
      file_storage_name: fileStorageName,
      isFile: true,
      fileDownloadName: fileDisplayName,
      openInNewTab: true,
      onSuccessFn: (data) => {
        try {
          const blob =
            data instanceof Blob
              ? data
              : new Blob([data], { type: contentType });
          setPreviewUrl(URL.createObjectURL(blob));
        } catch (e) {
          console.error("Blob URL error:", e);
          setError("Unable to process file preview.");
        } finally {
          setLoading(false);
        }
      },
      onErrorFn: (err) => {
        console.error("Fetch error:", err);
        setError("Failed to load file.");
        setLoading(false);
      },
    });
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height,
        width,
        background: "#fff",
        position: "relative",
        overflow: "hidden",
        ...sx,
      }}
    >
      {/* Floating "open in new tab" button (top-right) */}
      <Tooltip title="Open in new tab">
        <IconButton
          loading={borrowerPortalHookLoading && !loading}
          onClick={handleOpenInNewTab}
          disabled={!canOpenInNewTab}
          size="small"
          sx={{
            position: "absolute",
            top: 8,
            right: 8,
            zIndex: 10,
            backgroundColor: "rgba(255, 255, 255, 0.72)",
            boxShadow: 2,
            "&:hover": { backgroundColor: "rgba(255,255,255,1)" },
            "&.MuiIconButton-loading": { backgroundColor: "#ffffff69" },
          }}
          aria-label="open in new tab"
        >
          <OpenInNew fontSize="small" />
        </IconButton>
      </Tooltip>

      <Box
        sx={{
          position: "relative",
          flexGrow: 1,
          bgcolor: (theme) => theme.palette.grey[50],
        }}
      >
        {loading && (
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              background: "rgba(0,0,0,0.6)",
            }}
          >
            <CircularProgress sx={{ color: "#fff" }} />
          </Box>
        )}

        {error && (
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: "rgba(255,255,255,0.8)",
              p: 2,
            }}
          >
            <Box sx={{ textAlign: "center" }}>
              <ErrorRounded color="error" sx={{ fontSize: 40 }} />
              <Typography color="error" sx={{ mt: 1 }}>
                {error}
              </Typography>
              <Button
                onClick={() => {
                  setError(null);
                  setLoading(true);
                }}
                sx={{ mt: 1 }}
              >
                Try Again
              </Button>
            </Box>
          </Box>
        )}

        {!loading && !error && previewUrl && (
          <>
            {isImage && (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  height: "100%",
                  background: "#282828",
                }}
              >
                <img
                  src={previewUrl}
                  alt={fileDisplayName}
                  style={{
                    maxWidth: "100%",
                    maxHeight: "100%",
                    objectFit: "contain",
                    border: "1px solid #fff8",
                    borderRadius: 2,
                  }}
                />
              </Box>
            )}

            {isPdf && (
              <iframe
                src={previewUrl}
                title={fileDisplayName}
                style={{ width: "100%", height: "100%", border: "none" }}
              />
            )}

            {!isImage && !isPdf && (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  height: "100%",
                  p: 3,
                }}
              >
                <FilePreviewImageTypeIcon
                  contentType={contentType}
                  color={"text.secondary"}
                />
                <Typography variant="body1" sx={{ mt: 2, textAlign: "center" }}>
                  Cannot preview this file type.
                </Typography>
              </Box>
            )}
          </>
        )}
      </Box>
    </Box>
  );
};

FilePreview.propTypes = {
  fileObject: PropTypes.object.isRequired,
  onClose: PropTypes.func,
  showDownloadButton: PropTypes.bool,
  height: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  width: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  sx: PropTypes.object,
  contentType: PropTypes.string,
  fileDisplayName: PropTypes.string,
  fileStorageName: PropTypes.string,
  loanId: PropTypes.string,
};

export default FilePreview;
